import Grid from '@mui/material/Unstable_Grid2/Grid2';
import React from 'react';
import {
  ChartDataAdapter,
  UtilizationData
} from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/Summary/Visualizations/adapters/types';
import BeaconProChart from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/Summary/Visualizations/BeaconProChart';
import {
  ContentCardContainer,
  ContentCardHeader,
  ContentUtilizationStatus,
  ContentTextContainer,
  ContentUtilizationImageCardPlaceholder,
  ContentUtilizationImageCard,
  ContentUtilizationVideoCard,
  ContentUtilizationVideoCardPlaceholder
} from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/components/UtilizationCardContainer';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { useMainEntity } from 'src/utils/Hooks/reduxSelectors';
import { SlExcludeSelectedCheckbox, SlSelectedCheckbox } from 'src/components/SvgIcons';

const getCardContent = (item: UtilizationData) => {
  switch (item.type) {
    case 'character':
      return (
        <ContentTextContainer textVariant="body1" textSx={{ lineHeight: '24px' }}>
          {item.content}
        </ContentTextContainer>
      );
    case 'image':
      return (
        <Grid container sx={{ rowGap: '24px', columnGap: '24px', justifyContent: 'space-evenly' }}>
          {item.content.map((image, index) =>
            image ? (
              <ContentUtilizationImageCard
                key={index}
                imageUri={image.url}
                mainDisplayTitle={image.altText}
                secondaryDisplayTitle={image.altText}
                metrics={{ resolution: image.resolution, ratio: image.ratio }}
              />
            ) : (
              <ContentUtilizationImageCardPlaceholder key={index} />
            )
          )}
        </Grid>
      );
    case 'video':
      return (
        <Grid container sx={{ rowGap: '24px', columnGap: '24px', justifyContent: 'space-evenly' }}>
          {item.content.map((video, index) =>
            video ? (
              <ContentUtilizationVideoCard
                key={index}
                videoSrc={video.url}
                metrics={{ resolution: video.resolution, length: video.length }}
              />
            ) : (
              <ContentUtilizationVideoCardPlaceholder key={index} />
            )
          )}
        </Grid>
      );
    case 'aPlusContent':
      return (
        <ContentTextContainer>
          <Flex flexDirection="row" alignItems="center">
            {item.currentUtilization ? (
              <SlSelectedCheckbox width={24} height={24} />
            ) : (
              <SlExcludeSelectedCheckbox width={24} height={24} />
            )}
            <div style={{ marginLeft: '8px' }}>
              {`The product details page currently ${
                item.currentUtilization ? 'has' : 'does not have'
              } Amazon A+ content.`}
            </div>
          </Flex>
        </ContentTextContainer>
      );
    default:
      return null;
  }
};

interface ProductLevelContentUtilizationSummaryProps {
  queryFn: () => Promise<ChartDataAdapter<UtilizationData[]>>;
  renderSubtitle: () => string | React.ReactNode;
}

export const ProductLevelContentUtilizationSummary = ({
  queryFn,
  renderSubtitle
}: ProductLevelContentUtilizationSummaryProps) => {
  const { type } = useMainEntity();

  if (type !== 'product') {
    return null;
  }

  return (
    <BeaconProChart name="product-level-content-utilization-summary" queryFn={queryFn}>
      <BeaconProChart.Header menuButton={null}>
        <BeaconProChart.Title>
          <Text variant="h4">Content Utilization</Text>
        </BeaconProChart.Title>
      </BeaconProChart.Header>
      <BeaconProChart.Subtitle
        render={(_, isLoading) => {
          if (isLoading) {
            return 'Loading subtitle...';
          }

          return renderSubtitle();
        }}
      />

      <BeaconProChart.Visualization
        render={(adapter: ChartDataAdapter<UtilizationData[]>) => {
          const data = adapter.transform();

          return (
            <Flex marginTop="32px" flexDirection="column" gap={24}>
              {data.map((item: UtilizationData, i: number) => (
                <ContentCardContainer key={i}>
                  <ContentCardHeader title={item.title}>
                    {item.type !== 'aPlusContent' && (
                      <ContentUtilizationStatus
                        currentUtilization={item.currentUtilization}
                        maxUtilization={item.maxUtilization}
                        score={item.score}
                        label={item.type as 'image' | 'video' | 'character'}
                      />
                    )}
                  </ContentCardHeader>
                  {getCardContent(item)}
                </ContentCardContainer>
              ))}
            </Flex>
          );
        }}
      />
    </BeaconProChart>
  );
};
